import React, { useMemo, useState } from "react";
import { createUser } from "./api";

const roles = [
  { value: "consultor", label: "Consultor" },
  { value: "cliente", label: "Cliente" },
];

const activeOptions = [
  { value: true, label: "Sí" },
  { value: false, label: "No" },
];

function ToggleGroup({ label, options, value, onChange }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-slate-700">{label}</p>
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
        {options.map((opt, idx) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                selected
                  ? "bg-blue-600 text-white"
                  : "text-slate-700 hover:bg-slate-100"
              } ${idx > 0 ? "border-l border-slate-200" : ""}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CreateUserScreen({ onExit, currentUser }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(roles[0].value);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canAccess = useMemo(
    () => currentUser && (currentUser.role === "admin" || currentUser.role === "Administrador" || currentUser.role === "administrador"),
    [currentUser]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const payload = {
        username: username.trim(),
        password,
        role,
        is_active: isActive,
      };
      const result = await createUser(payload);
      setSuccess(`Usuario creado: ${result?.username || payload.username}`);
      setUsername("");
      setPassword("");
      setRole(roles[0].value);
      setIsActive(true);
    } catch (err) {
      setError(err?.message || "No se pudo crear el usuario");
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-xl p-8 text-center space-y-4">
          <h1 className="text-2xl font-bold">Acceso restringido</h1>
          <p className="text-slate-300 text-sm">
            Esta sección solo está disponible para usuarios con rol admin.
          </p>
          <button
            onClick={onExit}
            className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white/95 backdrop-blur rounded-2xl shadow-2xl border border-slate-200 p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase font-semibold tracking-[0.25em] text-slate-500">
              Crear usuarios
            </p>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Nuevo usuario</h1>
            <p className="text-sm text-slate-600 mt-1">
              Envía un POST a /api/users.php con tu token admin activo.
            </p>
          </div>
          <button
            onClick={onExit}
            className="text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            Volver
          </button>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Usuario</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                placeholder="nuevo_usuario"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Contraseña</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                placeholder="ContraseñaSegura123"
              />
            </div>
          </div>

          <ToggleGroup
            label="Rol"
            options={roles}
            value={role}
            onChange={setRole}
          />

          <ToggleGroup
            label="Activo"
            options={activeOptions}
            value={isActive}
            onChange={setIsActive}
          />

          {error && (
            <div className="rounded-lg bg-rose-50 text-rose-700 px-4 py-3 text-sm border border-rose-100">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg bg-emerald-50 text-emerald-700 px-4 py-3 text-sm border border-emerald-100">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full px-6 py-3 rounded-lg font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Creando..." : "Crear usuario"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateUserScreen;
